<script setup lang="ts">
import type { Ref } from "vue"
import type { Media } from "@/models"
import type { IReactionRequestV1 } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { useSwipe } from "@vueuse/core"
import { computed, inject, onBeforeUnmount, ref, useTemplateRef, watchEffect } from "vue"
import { useAddReaction } from "@/api"
import { ReactionButton } from "@/components"
import { Button } from "@/components/ui"
import { useMediaReaction, useTranslation } from "@/composables"
import { MediaType } from "@/models"
import { EventVaultIdKey } from "@/providers/keys"
import { safelyUnloadVideoElement } from "@/utils"

interface FeedTileProps {
  media: Media
  areReactionsEnabled?: boolean
  areCommentsEnabled?: boolean
}

const props = withDefaults(defineProps<FeedTileProps>(), {
  areReactionsEnabled: false,
  areCommentsEnabled: false,
})

const emit = defineEmits<{
  (e: "viewComments", mediaItem: Media): void
  (e: "click", mediaItem: Media): void
}>()

const eventVaultId = inject(EventVaultIdKey, undefined)

const currentMedia = computed(() => props.media)
const isImageLoaded: Ref<boolean> = ref(false)
const feedTile: Ref<HTMLElement | null> = ref(null)
const videoRef = useTemplateRef<HTMLVideoElement | null>("videoRef")

onBeforeUnmount(() => {
  safelyUnloadVideoElement(videoRef.value)
})

const { isSwiping } = useSwipe(feedTile)

const { t } = useTranslation()

const {
  incrementedReactionCount,
  currentReactionCount,
  hasUserReacted,
  mediaId: currentMediaIdToReact,
  mediaType: currentMediaTypeToReact,
  incrementTotalReactCount,
  clearReactionData,
} = useMediaReaction(currentMedia)

function viewMediaComments() {
  emit("viewComments", currentMedia.value)
}

function handleImageLoad() {
  isImageLoaded.value = true
}

const client = useQueryClient()

const { mutate: mutateReaction } = useAddReaction()

function postReaction() {
  if (!eventVaultId || !incrementedReactionCount.value || !props.areReactionsEnabled) {
    return
  }

  const reaction: IReactionRequestV1 = {
    contentID: currentMediaIdToReact.value,
    contentType: currentMediaTypeToReact.value,
    type: "love",
    quantity: incrementedReactionCount.value,
  }

  mutateReaction({ reaction, eventVaultId }, {
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["listMedia"] })
    },
  })

  clearReactionData()
}

watchEffect(() => {
  if (isSwiping.value) {
    if (!incrementedReactionCount.value || !props.areReactionsEnabled) {
      return
    }

    postReaction()
  }
})
</script>

<template>
  <div ref="feedTile" class="w-full">
    <div
      v-if="!isImageLoaded"
      class="gpx-skeleton w-full h-[400px] md:min-w-[480px] rounded-none"
    />

    <button
      data-test="gpx-feed-tile-btn"
      class="relative flex bg-transparent p-0 border-none cursor-pointer w-full m-0"
      @click="() => emit('click', media)"
    >
      <div v-if="media?.type === MediaType.Vid">
        <video
          v-show="isImageLoaded"
          ref="videoRef"
          playsinline
          :poster="media?.getThumbnailUrl()"
          controls
          controlsList="nodownload nofullscreen"
          disablePictureInPicture
          @contextmenu.prevent
          @canplay="handleImageLoad"
          @loadedmetadata="handleImageLoad"
        >
          <source :src="media?.thumbnailUrl" type="video/mp4">
        </video>
      </div>

      <div v-else-if="media?.type === MediaType.Pic">
        <picture v-show="isImageLoaded" @contextmenu.prevent>
          <img
            :src="media?.getThumbnailUrl()"
            :alt="t('photo')"
            @load="handleImageLoad"
          >
        </picture>
      </div>
    </button>

    <div
      class="w-full py-2 pb-10 font-montserrat flex flex-col gap-2 text-sm md:text-base"
    >
      <div class="flex gap-4 font-comfortaa font-semibold">
        <ReactionButton
          v-if="props.areReactionsEnabled"
          data-test="gpx-feed-tile-reaction-button"
          type="love"
          count-placement="side"
          icon-variant="outline"
          no-animation
          :total-react-count="currentReactionCount"
          :user-has-reacted="hasUserReacted"
          @on-react="incrementTotalReactCount"
          @mouseleave="postReaction"
          @focusout="postReaction"
        />

        <div v-if="props.areCommentsEnabled" class="flex gap-2 items-center">
          <Button
            variant="flat"
            prefix-icon="ChatText"
            class="p-0"
            data-test="gpx-feed-tile-comment-button"
            @click="viewMediaComments"
          >
            {{ media.totalComments }}
          </Button>
        </div>
      </div>

      <div>
        <p v-if="media?.title || media?.description">
          {{ media.description || media.title }}
        </p>
        <h4 class="font-semibold italic">
          {{ media?.uploadedBy }}
        </h4>
      </div>

      <div
        v-if="
          props.areCommentsEnabled
            && media?.totalComments
            && media?.totalComments > 0
        "
      >
        <Button
          variant="flat"
          class="text-label font-semibold p-0 text-sm"
          @click="viewMediaComments"
        >
          {{ t("gallery_feed.view_comments") }}
        </Button>
      </div>
    </div>
  </div>
</template>
